/**
 * Today + counts — V98-1-7 / ADR-0046 §2; PRD G-2, G-3, §6; research §2
 * items 1–3, 14, 23.
 *
 * One composer, one counter:
 *
 * - `collectWorkspaceWork` enumerates every open, actionable thing in the
 *   workspace once, each with the person who owes the **next action** on it
 *   right now (the holder), the hat that action is owed under, and the people
 *   waiting on it. Sources (every hat — there is no role switcher):
 *     signature requests (routed approver; their delegate while a delegation
 *       covers it; the preparer when nobody is eligible),
 *     access reviews (per-row reviewer / approver routing, the assigned
 *       reviewer to complete; `needs_independent_party` rows),
 *     work items the person owns (asks delivered to them, independent checks,
 *       escalations, send-backs, dispute resolutions, derived work) — an
 *       absent owner's delegable items go to their live delegate,
 *     open independent checks + unacknowledged check escalations,
 *     open coverage asks,
 *     owned open findings and owned obligations' next open occurrence.
 * - Every entry carries an **item key** — the business item it acts on
 *   (`policy:pol-1`, `access_review:ar-1`, `independent_check:chk-1`, …), so
 *   an item a person both owns and approves is one row, under the next action
 *   they owe (ACTION_RANK), tagged with every hat they have on it.
 * - `countWork` is THE shared counter: Today, `/me/counts`, the sidebar and
 *   the program pages all count through it, so the numbers are equal by
 *   construction (PRD §6).
 * - V98-3 review S8 — ONE definition of "open": an item (key) with at least
 *   one holder-assigned next action, counted once however many people hold
 *   a piece of it. Each open item has ONE primary holder (its most urgent
 *   next action: ACTION_RANK, then urgency, then user id) and ONE primary
 *   program (its first program id), so the scopes reconcile exactly:
 *     org.open = Σ by_owner.open
 *              = Σ per_program (open − shared_elsewhere) + org.no_program.
 *   `yours` is always the viewer's own owed items.
 * - S9 — the next hard date names the projected miss (`will_miss`: item,
 *   date, reason), and an approver sees `sign_before_leaving`: non-delegable
 *   signatures they owe whose hard date falls inside their own pending or
 *   confirmed absence.
 *
 * Scope: `owed` and `waiting` only ever contain the viewer's own work. Org
 * and per-program totals are counts, shown only to owners / admins (all
 * programs) and program leads (their programs).
 *
 * Read-only. The workspace always comes from the caller (the route's
 * session context), never from an id inside an item.
 */
import { getProgramManifest, legalDeterminationsProgram, programHas } from '../programs/manifests.js';
import { conmonCycleHolder, conmonCycleHref, conmonCycleTitle, conmonDueInstant, listDueConmonCycles } from './govramp-conmon-service.js';
import { listHats } from './assignment-service.js';
import { absenceOn, activeDelegationFor, addDays, effectiveDelegationFor, isNonDelegable, routeReviewRows, utcDay, } from './sod-service.js';
import { currentRowApprovals } from './access-review-service.js';
import { accessReviewTitle, checkFollowUpFacts, coverageItemTitle, signaturesAtRisk } from './coverage-service.js';
import { describeWorkItem } from './work-item-service.js';
import { periodsOfRecord } from './period-of-record-service.js';
import { computeScopeVerdict, isGap } from './program-verdict-service.js';
import { AI_LEGAL_ASK, AI_OWNER_REDIRECT, AI_POLICY_ASK } from './ai-program-ask-service.js';
import { legalReviewers } from './obligation-source-service.js';
import { buildGrid, legalTodaySummary } from './applicability-determination-service.js';
import { unsignedCompleteTargetPlans } from './infra-plan-service.js';
// ---- types -----------------------------------------------------------------------
/** The program legal determinations live in (AI Regulatory): its scope page holds the grid. */
const LEGAL_PROGRAM = legalDeterminationsProgram() ?? '';
/** What the holder has to do next. Lower rank = owed first when one item carries several actions. */
export const ACTION_RANK = {
    answer_ask: 1,
    sign: 2,
    /** V98-4-2: a fact question from Legal to the system owner (stored verbatim, attested). */
    answer_fact: 2,
    /** V98-4-2: an applicability determination (law × system) for the legal reviewer. */
    decide: 3,
    approve_rows: 3,
    review_rows: 3,
    conclude_check: 3,
    handle_escalation: 3,
    resolve_dispute: 3,
    add_independent_party: 4,
    find_approver: 4,
    /** V98-4-1: an obligation-source proposal awaiting the legal reviewer. */
    review_proposal: 4,
    revise: 5,
    complete_review: 6,
    remediate: 7,
    complete_occurrence: 7,
    complete: 8,
};
// ---- helpers -----------------------------------------------------------------------
// V98-4-2: 'determination' — an in-force gap work item and the cell's decision are ONE item (counted once).
const PROGRAM_ITEM_CONSUMERS = ['determination', 'policy', 'scn', 'access_review', 'finding'];
const VIA_HAT = {
    holder: 'approver', backup: 'backup_approver', delegate: 'delegate', escalation: 'escalation',
    alternate_signer: 'alternate_signer', workspace_admin: 'workspace_admin', independent_party: 'independent_party',
};
/** The hat a routed step is owed under: the hat actually held, or how it was reached. */
function routedHat(via, responsibility) {
    return (via === 'holder' || via === 'backup') && responsibility ? responsibility : VIA_HAT[via] ?? 'approver';
}
function minDate(...d) {
    const v = d.filter(Boolean);
    return v.length ? v.sort()[0] : null;
}
function isOverdue(e, nowIso) {
    return (!!e.due_at && e.due_at < nowIso) || (!!e.hard_date && e.hard_date < nowIso);
}
/** Overdue first; then the nearest due / hard date; ties: blocks a filing (hard date), earlier hard date, key. */
function urgency(a, b, nowIso) {
    const ao = isOverdue(a, nowIso), bo = isOverdue(b, nowIso);
    if (ao !== bo)
        return ao ? -1 : 1;
    const ad = minDate(a.due_at, a.hard_date), bd = minDate(b.due_at, b.hard_date);
    if (ad !== bd)
        return ad === null ? 1 : bd === null ? -1 : ad < bd ? -1 : 1;
    if (!!a.hard_date !== !!b.hard_date)
        return a.hard_date ? -1 : 1;
    if (a.hard_date && b.hard_date && a.hard_date !== b.hard_date)
        return a.hard_date < b.hard_date ? -1 : 1;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}
function selectedOf(routingJson) {
    try {
        const t = JSON.parse(routingJson ?? '[]');
        return Array.isArray(t) ? t.find((x) => x.outcome === 'selected') ?? null : null;
    }
    catch {
        return null;
    }
}
async function programIndex(db, ws) {
    const [profiles, periods] = await Promise.all([
        db.query(`SELECT profile_id, program, status FROM certification_profiles WHERE workspace_id = ?`, [ws]),
        db.query(`SELECT period_id, profile_id, period_start, period_end FROM audit_periods WHERE workspace_id = ?`, [ws]),
    ]);
    return {
        programKey: new Map(profiles.results.map((p) => [p.profile_id, p.program])),
        profiles: new Set(profiles.results.map((p) => p.profile_id)),
        frameworkProfile: new Map(profiles.results.filter((p) => p.status === 'active').map((p) => [p.program, p.profile_id])),
        periodProfile: new Map(periods.results.map((p) => [p.period_id, p.profile_id])),
        periods: periods.results,
    };
}
/** V98-2-2 / ADR-0048 §3: the periods (one per program) an occurrence due on `dueAt` counts in. */
function recordPeriods(idx, dueAt) {
    if (!dueAt)
        return { periods: [], programs: [] };
    const hits = periodsOfRecord(dueAt, idx.periods);
    return { periods: hits.map((h) => h.period_id), programs: [...new Set(hits.map((h) => h.profile_id))].sort() };
}
function programOfSubject(idx, kind, id) {
    if (kind === 'program' && idx.profiles.has(id))
        return [id];
    if (kind === 'period') {
        const p = idx.periodProfile.get(id);
        return p ? [p] : [];
    }
    return [];
}
function periodOfSubject(idx, kind, id) {
    return kind === 'period' && idx.periodProfile.has(id) ? [id] : [];
}
/** The holder of an owned item today: the owner, or their live delegate while they are away. */
async function holderOf(db, ws, owner, category, now) {
    if (category && (await absenceOn(db, ws, owner, utcDay(now)))) {
        const d = await activeDelegationFor(db, ws, owner, category, now);
        if (d)
            return { holder: d.delegate_user_id, on_behalf_of: owner, delegation_id: d.delegation_id };
    }
    return { holder: owner, on_behalf_of: null, delegation_id: null };
}
// ---- the composer ----------------------------------------------------------------------
/** Every open, actionable item in the workspace with its next-action holder. Read-only. */
export async function collectWorkspaceWork(db, workspaceId, now = new Date()) {
    const iso = now.toISOString();
    // V115-3-8 (v11.5 gate 3): every section's top-level read starts here and runs concurrently; each is
    // awaited where it always was, so the processing below and its order are unchanged. Each query is a
    // Worker → database round trip (~65 ms to Neon from a West-coast edge, 2026-10-27), and these reads
    // ran one after another (16 sequential waves on an empty workspace). The no-op catch only stops an
    // early failure surfacing as unhandled before its own await, which still throws (sections 11 and 12
    // keep swallowing theirs in their try blocks).
    const early = (p) => { p.catch(() => undefined); return p; };
    const pIdx = early(programIndex(db, workspaceId));
    const pSigs = early(db.query(`SELECT request_id, artifact_kind, artifact_id, title, preparer_user_id, requested_by_user_id, approver_subject_kind, approver_subject_id,
            routing_status, approver_user_id, routing_json, due_at, hard_date
       FROM signature_requests WHERE workspace_id = ? AND state = 'requested' ORDER BY created_at ASC, request_id ASC`, [workspaceId]));
    const pReviews = early(db.query(`SELECT v.review_id, v.population_id, p.period_id, o.due_at FROM access_reviews v
       LEFT JOIN populations p ON p.population_id = v.population_id AND p.workspace_id = v.workspace_id
       LEFT JOIN obligation_occurrences o ON o.occurrence_id = v.occurrence_id AND o.workspace_id = v.workspace_id
      WHERE v.workspace_id = ? AND v.status = 'open' ORDER BY v.created_at ASC, v.review_id ASC`, [workspaceId]));
    const pItems = early(db.query(`SELECT w.work_item_id, w.title, w.kind, w.due_at, w.hard_date, w.source_kind, w.source_id, w.cause_kind, w.created_by, a.user_id
       FROM work_items w
       JOIN assignments a ON a.workspace_id = w.workspace_id AND a.subject_kind = 'work_item' AND a.subject_id = w.work_item_id
      WHERE w.workspace_id = ? AND w.status = 'open' AND a.responsibility = 'owner'
        AND a.effective_from <= ? AND (a.effective_to IS NULL OR a.effective_to > ?)
      ORDER BY w.created_at ASC, w.work_item_id ASC`, [workspaceId, iso, iso]));
    const pLinks = early(db.query(`SELECT l.work_item_id, l.consumer_kind, l.consumer_id, l.period_id FROM work_item_links l
       JOIN work_items w ON w.work_item_id = l.work_item_id AND w.workspace_id = l.workspace_id
      WHERE l.workspace_id = ? AND w.status = 'open' ORDER BY l.consumer_kind ASC, l.consumer_id ASC`, [workspaceId]));
    const pChecks = early(db.query(`SELECT k.check_id, k.checker_user_id, k.due_at, k.escalated_to_user_id, k.escalation_acknowledged_at,
            c.preparer_user_id, c.item_kind, c.item_id, c.item_version, c.hard_date
       FROM independent_checks k JOIN compensating_reviews c ON c.comp_review_id = k.comp_review_id AND c.workspace_id = k.workspace_id
      WHERE k.workspace_id = ? AND k.conclusion IS NULL AND k.superseded_at IS NULL ORDER BY k.due_at ASC, k.check_id ASC`, [workspaceId]));
    const pAsks = early(db.query(`SELECT a.ask_id, a.kind, a.from_user_id, a.to_user_id, a.item_kind, a.item_id, a.delivered_at, a.window_hours, b.starts_on, b.ends_on
       FROM coverage_asks a LEFT JOIN absences b ON b.absence_id = a.absence_id AND b.workspace_id = a.workspace_id
      WHERE a.workspace_id = ? AND a.state = 'open' ORDER BY a.created_at ASC, a.ask_id ASC`, [workspaceId]));
    const pFindings = early(db.query(`SELECT f.finding_id, f.control_id, f.detail, f.sla_deadline_at, f.certification_program, a.user_id FROM findings f
       JOIN assignments a ON a.workspace_id = f.workspace_id AND a.subject_kind = 'finding' AND a.subject_id = f.finding_id
      WHERE f.workspace_id = ? AND f.status IN ('open', 'acknowledged') AND a.responsibility = 'owner'
        AND a.effective_from <= ? AND (a.effective_to IS NULL OR a.effective_to > ?)
      ORDER BY f.sla_deadline_at ASC, f.finding_id ASC`, [workspaceId, iso, iso]));
    const pOcc = early(db.query(`SELECT x.occurrence_id, x.obligation_id, o.title, x.due_at, a.user_id FROM obligation_occurrences x
       JOIN recurring_obligations o ON o.obligation_id = x.obligation_id AND o.workspace_id = x.workspace_id
       JOIN assignments a ON a.workspace_id = o.workspace_id AND a.subject_kind = 'obligation' AND a.subject_id = o.obligation_id
      WHERE x.workspace_id = ? AND x.status = 'open' AND o.status = 'active' AND a.responsibility = 'owner'
        AND a.effective_from <= ? AND (a.effective_to IS NULL OR a.effective_to > ?)
      ORDER BY x.due_at ASC, x.occurrence_id ASC`, [workspaceId, iso, iso]));
    const pProposals = early(db.query(`SELECT decision_id, note, proposed_by, proposed_at, profile_id FROM obligation_source_decisions
      WHERE workspace_id = ? AND kind = 'proposal' AND state = 'proposed' ORDER BY proposed_at ASC, decision_id ASC`, [workspaceId]));
    const pCycles = early(listDueConmonCycles(db, workspaceId, { now, horizonDays: 30 }));
    const pPlans = early(unsignedCompleteTargetPlans(db, workspaceId));
    const pPending = early(db.query(`SELECT c.profile_id, c.program, d.period_id, d.proposed_by, COUNT(*) AS n
       FROM determinations d
       JOIN audit_periods p ON p.period_id = d.period_id AND p.workspace_id = d.workspace_id
       JOIN certification_profiles c ON c.profile_id = p.profile_id AND c.workspace_id = p.workspace_id
      WHERE d.workspace_id = ? AND d.state = 'proposed' AND d.kind = 'self' AND p.status <> 'closed' AND c.status = 'active'
      GROUP BY c.profile_id, c.program, d.period_id, d.proposed_by
      ORDER BY c.profile_id, d.period_id`, [workspaceId]));
    const idx = await pIdx;
    const entries = [];
    const base = (e) => ({
        on_behalf_of_user_id: null, delegation_id: null, due_at: null, hard_date: null, blocked: false, waiting_user_ids: [],
        program_ids: [], period_ids: [], quantity: 1, ...e,
    });
    // 1. Signature requests.
    const sigs = await pSigs;
    const sigPrograms = new Map();
    const sigPeriods = new Map();
    const sigTitle = new Map();
    for (const r of sigs.results) {
        sigTitle.set(r.request_id, r.title);
        const programs = programOfSubject(idx, r.approver_subject_kind, r.approver_subject_id);
        const periods = periodOfSubject(idx, r.approver_subject_kind, r.approver_subject_id);
        sigPrograms.set(r.request_id, programs);
        sigPeriods.set(r.request_id, periods);
        const key = `${r.artifact_kind}:${r.artifact_id}`;
        const ref = { kind: 'signature_request', id: r.request_id };
        const parties = [...new Set([r.preparer_user_id, r.requested_by_user_id])];
        if (r.routing_status !== 'routed' || !r.approver_user_id) {
            entries.push(base({ key, ref, title: r.title, action: 'find_approver', hat: 'preparer', holder_user_id: r.preparer_user_id, blocked: true,
                due_at: r.due_at, hard_date: r.hard_date, waiting_user_ids: parties.filter((p) => p !== r.preparer_user_id), program_ids: programs, period_ids: periods }));
            continue;
        }
        const sel = selectedOf(r.routing_json);
        let holder = r.approver_user_id;
        let hat = sel ? routedHat(sel.via, sel.responsibility) : 'approver';
        let onBehalf = null;
        let delegationId = null;
        if (sel?.via === 'delegate' && sel.user_id) {
            holder = sel.user_id;
            onBehalf = r.approver_user_id;
            delegationId = sel.delegation_id ?? null;
        }
        else {
            // A delegation in effect (the approver's confirmed absence has started —
            // S1/S2) locks the approver of record out (they "take it back" to act).
            const d = await effectiveDelegationFor(db, workspaceId, r.approver_user_id, r.artifact_kind, now);
            if (d) {
                holder = d.delegate_user_id;
                onBehalf = r.approver_user_id;
                delegationId = d.delegation_id;
                hat = 'delegate';
            }
        }
        entries.push(base({
            key, ref, title: r.title, action: 'sign', hat, holder_user_id: holder, on_behalf_of_user_id: onBehalf, delegation_id: delegationId,
            due_at: r.due_at, hard_date: r.hard_date, program_ids: programs, period_ids: periods,
            waiting_user_ids: [...parties, ...(onBehalf ? [onBehalf] : [])].filter((p) => p !== holder),
        }));
    }
    // 2. Access reviews: per-row routing, grouped per holder and action.
    const reviews = await pReviews;
    for (const v of reviews.results) {
        const key = `access_review:${v.review_id}`;
        const ref = { kind: 'access_review', id: v.review_id };
        const title = await accessReviewTitle(db, workspaceId, v.review_id);
        // Period of record: the linked occurrence's due date (one rule); a review
        // without an occurrence counts in its population's period.
        const rec = recordPeriods(idx, v.due_at);
        const popPeriod = !v.due_at && v.period_id && idx.periodProfile.has(v.period_id) ? v.period_id : null;
        const programs = popPeriod ? [idx.periodProfile.get(popPeriod)] : rec.programs;
        const periods = popPeriod ? [popPeriod] : rec.periods;
        const approved = await currentRowApprovals(db, workspaceId, v.review_id);
        const rows = await routeReviewRows(db, workspaceId, v.review_id, now, { approved: new Set(approved.keys()) });
        const groups = new Map();
        const bump = (holder, action, hat, extra = {}) => {
            const g = `${holder ?? ''}|${action}|${hat}|${extra.on_behalf_of_user_id ?? ''}`;
            const cur = groups.get(g);
            if (cur) {
                cur.quantity++;
                return;
            }
            groups.set(g, base({ key, ref, title, action, hat, holder_user_id: holder, due_at: v.due_at, hard_date: v.due_at, program_ids: programs, period_ids: periods, ...extra }));
        };
        const assigned = (await db.query(`SELECT user_id FROM assignments WHERE workspace_id = ? AND subject_kind = 'access_review' AND subject_id = ? AND responsibility = 'access_reviewer'
          AND effective_from <= ? AND (effective_to IS NULL OR effective_to > ?) ORDER BY effective_from ASC, user_id ASC`, [workspaceId, v.review_id, iso, iso])).results.map((a) => a.user_id);
        for (const row of rows) {
            if (row.reviewer) {
                const rr = row.reviewer;
                if (rr.status === 'needs_independent_party') {
                    for (const a of assigned)
                        bump(a, 'add_independent_party', 'access_reviewer', { blocked: true, reasons: rr.reasons });
                }
                else if (rr.status === 'routed') {
                    // S4: a stand-in reviewer (an approver-hat holder reached because the
                    // assigned reviewer is the row's subject) reviews under the reviewer
                    // hat — never "approver" / "backup approver" for a row they review;
                    // the approval of that row then routes past them (T15).
                    const reviewHat = rr.via === 'holder' || rr.via === 'backup' ? 'access_reviewer' : routedHat(rr.via, rr.responsibility);
                    bump(rr.actor_user_id, 'review_rows', reviewHat, { on_behalf_of_user_id: rr.on_behalf_of_user_id, delegation_id: rr.delegation_id });
                }
                else {
                    for (const a of assigned)
                        bump(a, 'review_rows', 'access_reviewer', { blocked: true, reasons: rr.reasons });
                }
            }
            else if (row.approver) {
                const ra = row.approver;
                if (ra.status === 'routed') {
                    bump(ra.actor_user_id, 'approve_rows', routedHat(ra.via, ra.responsibility), { on_behalf_of_user_id: ra.on_behalf_of_user_id, delegation_id: ra.delegation_id, waiting_user_ids: assigned });
                }
                else {
                    // Nobody can approve: the reviewer owes the next step (ask / compensating review).
                    for (const a of assigned)
                        bump(a, 'approve_rows', 'access_reviewer', { blocked: true, reasons: ra.reasons });
                }
            }
        }
        if (rows.length > 0 && rows.every((r) => !r.reviewer && !r.approver)) {
            for (const a of assigned)
                bump(a, 'complete_review', 'access_reviewer');
        }
        entries.push(...groups.values());
    }
    // 3. Work items the person owns (asks, checks, escalations, send-backs, disputes, derived, tasks).
    const items = await pItems;
    const links = await pLinks;
    const linksOf = new Map();
    for (const l of links.results)
        linksOf.set(l.work_item_id, [...(linksOf.get(l.work_item_id) ?? []), l]);
    for (const w of items.results) {
        // Asks, checks and escalations are read from their own records below (their
        // state is authoritative; a delivered-ask work item is only its carrier).
        if (w.source_id && ['coverage_ask', 'independent_check', 'independent_check_escalation'].includes(w.source_kind ?? ''))
            continue;
        const ls = linksOf.get(w.work_item_id) ?? [];
        const programs = new Set();
        const periods = new Set();
        for (const l of ls) {
            const pid = l.period_id && idx.periodProfile.has(l.period_id) ? l.period_id
                : l.consumer_kind === 'audit_period' && idx.periodProfile.has(l.consumer_id) ? l.consumer_id : null;
            if (pid) {
                programs.add(idx.periodProfile.get(pid));
                periods.add(pid);
            }
            if (l.consumer_kind === 'program' && idx.profiles.has(l.consumer_id))
                programs.add(l.consumer_id);
        }
        let key = `work_item:${w.work_item_id}`;
        let action = w.kind === 'derived' ? 'remediate' : 'complete';
        let hat = 'owner';
        let category = 'work_item';
        const src = w.source_id;
        switch (w.source_kind) {
            case 'compensating_review':
                if (src)
                    key = `compensating_review:${src}`;
                action = 'resolve_dispute';
                hat = 'escalation';
                category = '';
                break;
            case 'signature_request': {
                // A send-back: the preparer revises the item the request was for.
                const r = src ? (await db.query(`SELECT artifact_kind, artifact_id, approver_subject_kind, approver_subject_id FROM signature_requests WHERE request_id = ? AND workspace_id = ? LIMIT 1`, [src, workspaceId])).results[0] : undefined;
                if (r) {
                    key = `${r.artifact_kind}:${r.artifact_id}`;
                    for (const p of programOfSubject(idx, r.approver_subject_kind, r.approver_subject_id))
                        programs.add(p);
                    for (const p of periodOfSubject(idx, r.approver_subject_kind, r.approver_subject_id))
                        periods.add(p);
                }
                action = 'revise';
                hat = 'preparer';
                break;
            }
            case AI_LEGAL_ASK:
            case AI_OWNER_REDIRECT:
            case AI_POLICY_ASK:
                // V98-4 review T4-B2 / T4-H8 / "Not me": an ask carried as a work item — its own item; the asker waits.
                action = 'answer_ask';
                hat = w.source_kind === AI_POLICY_ASK ? 'workspace_admin' : 'legal_reviewer';
                category = '';
                break;
            default: {
                const item = ls.find((l) => PROGRAM_ITEM_CONSUMERS.includes(l.consumer_kind));
                if (item)
                    key = `${item.consumer_kind}:${item.consumer_id}`;
            }
        }
        const h = await holderOf(db, workspaceId, w.user_id, category, now);
        const programList = [...programs].sort();
        const desc = describeWorkItem({ work_item_id: w.work_item_id, kind: w.kind, source_kind: w.source_kind, source_id: w.source_id, cause_kind: w.cause_kind }, ls.map((l) => ({ consumer_kind: l.consumer_kind, consumer_id: l.consumer_id })), programList.map((p) => idx.programKey.get(p)).filter(Boolean));
        entries.push(base({
            key, ref: { kind: 'work_item', id: w.work_item_id }, title: w.title, action, hat: h.on_behalf_of ? 'delegate' : hat,
            holder_user_id: h.holder, on_behalf_of_user_id: h.on_behalf_of, delegation_id: h.delegation_id,
            due_at: w.due_at, hard_date: w.hard_date, program_ids: programList, period_ids: [...periods].sort(),
            waiting_user_ids: [...(h.on_behalf_of ? [h.on_behalf_of] : []), ...(action === 'answer_ask' && w.created_by && w.created_by !== h.holder ? [w.created_by] : [])],
            subtype: desc.subtype, act: desc.act,
        }));
    }
    // 4. Independent checks (the checker) and their escalations; the self-approver waits.
    const checks = await pChecks;
    for (const k of checks.results) {
        const key = `independent_check:${k.check_id}`;
        const ref = { kind: 'independent_check', id: k.check_id };
        const programs = k.item_kind === 'signature_request' ? sigPrograms.get(k.item_id) ?? [] : [];
        const periods = k.item_kind === 'signature_request' ? sigPeriods.get(k.item_id) ?? [] : [];
        // S12: human titles from the item checked (never "<kind> <id>"); `about` carries the reference + version.
        const about = { kind: k.item_kind, id: k.item_id, title: await coverageItemTitle(db, workspaceId, k.item_kind, k.item_id), version: k.item_version };
        entries.push(base({ key, ref, title: `Independent check · ${about.title}`, action: 'conclude_check', hat: 'checker', about,
            holder_user_id: k.checker_user_id, due_at: k.due_at, hard_date: k.hard_date, waiting_user_ids: [k.preparer_user_id], program_ids: programs, period_ids: periods }));
        if (k.escalated_to_user_id && !k.escalation_acknowledged_at) {
            entries.push(base({ key, ref, title: `Overdue independent check · ${about.title}`, action: 'handle_escalation', hat: 'escalation', about,
                escalation: await checkFollowUpFacts(db, workspaceId, k.check_id),
                holder_user_id: k.escalated_to_user_id, due_at: k.due_at, hard_date: k.hard_date, program_ids: programs, period_ids: periods }));
        }
    }
    // 5. Open coverage asks: delivered to the recipient (even while away); the asker waits.
    const asks = await pAsks;
    for (const a of asks.results) {
        const due = a.delivered_at ? new Date(Date.parse(a.delivered_at) + a.window_hours * 3_600_000).toISOString() : null;
        // S12: human titles — what you're asked to do, about which item (never "<kind> <id>").
        const itemTitle = a.item_kind && a.item_id ? (sigTitle.get(a.item_id) ?? await coverageItemTitle(db, workspaceId, a.item_kind, a.item_id)) : null;
        const about = a.item_kind && a.item_id ? { kind: a.item_kind, id: a.item_id, title: itemTitle, version: null } : undefined;
        entries.push(base({
            key: `coverage_ask:${a.ask_id}`, ref: { kind: 'coverage_ask', id: a.ask_id }, ...(about ? { about } : {}),
            title: a.kind === 'sign_remote' ? `Sign remotely · ${itemTitle}`
                : a.kind === 'delegate' ? `Delegate or decline · ${itemTitle}`
                    : `Confirm absence · ${a.starts_on ?? ''} – ${a.ends_on ?? ''}`,
            action: 'answer_ask', hat: 'ask', holder_user_id: a.to_user_id, due_at: due, waiting_user_ids: [a.from_user_id],
            program_ids: a.item_kind === 'signature_request' && a.item_id ? sigPrograms.get(a.item_id) ?? [] : [],
            period_ids: a.item_kind === 'signature_request' && a.item_id ? sigPeriods.get(a.item_id) ?? [] : [],
        }));
    }
    // 6. Owned open findings.
    const findings = await pFindings;
    for (const f of findings.results) {
        const h = await holderOf(db, workspaceId, f.user_id, 'finding', now);
        const p = f.certification_program ? idx.frameworkProfile.get(f.certification_program) : undefined;
        entries.push(base({
            key: `finding:${f.finding_id}`, ref: { kind: 'finding', id: f.finding_id }, title: `Finding · ${f.control_id}`,
            action: 'remediate', hat: h.on_behalf_of ? 'delegate' : 'owner', holder_user_id: h.holder, on_behalf_of_user_id: h.on_behalf_of,
            delegation_id: h.delegation_id, due_at: f.sla_deadline_at, program_ids: p ? [p] : [], waiting_user_ids: h.on_behalf_of ? [h.on_behalf_of] : [],
        }));
    }
    // 7. Owned obligations: the earliest open occurrence.
    const occ = await pOcc;
    const seenObligation = new Set();
    for (const x of occ.results) {
        if (seenObligation.has(`${x.obligation_id}|${x.user_id}`))
            continue;
        seenObligation.add(`${x.obligation_id}|${x.user_id}`);
        const h = await holderOf(db, workspaceId, x.user_id, 'obligation', now);
        const rec = recordPeriods(idx, x.due_at);
        entries.push(base({
            key: `obligation:${x.obligation_id}`, ref: { kind: 'obligation_occurrence', id: x.occurrence_id }, title: x.title,
            action: 'complete_occurrence', hat: h.on_behalf_of ? 'delegate' : 'owner', holder_user_id: h.holder, on_behalf_of_user_id: h.on_behalf_of,
            delegation_id: h.delegation_id, due_at: x.due_at, waiting_user_ids: h.on_behalf_of ? [h.on_behalf_of] : [],
            program_ids: rec.programs, period_ids: rec.periods,
        }));
    }
    // 8. V98-4-1: open obligation-source proposals → the legal reviewer (never a decision; the proposer waits).
    const proposals = await pProposals;
    if (proposals.results.length) {
        const legal = await legalReviewers(db, workspaceId, now);
        const aiProfile = idx.frameworkProfile.get(LEGAL_PROGRAM) ?? null;
        const fallback = legal[0] ? null : (await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND role IN ('owner', 'admin') AND removed_at IS NULL
        ORDER BY CASE role WHEN 'owner' THEN 0 ELSE 1 END, joined_at ASC, user_id ASC LIMIT 1`, [workspaceId])).results[0]?.user_id ?? null;
        for (const p of proposals.results) {
            const holder = legal[0] ?? fallback;
            const programs = p.profile_id && idx.profiles.has(p.profile_id) ? [p.profile_id] : aiProfile ? [aiProfile] : [];
            entries.push(base({
                key: `obligation_source_proposal:${p.decision_id}`, ref: { kind: 'obligation_source_proposal', id: p.decision_id },
                title: p.note ?? 'AI-law proposal awaiting Legal', action: 'review_proposal', hat: 'legal_reviewer', holder_user_id: holder,
                blocked: !holder, waiting_user_ids: p.proposed_by && !p.proposed_by.startsWith('system:') ? [p.proposed_by] : [], program_ids: programs,
                subtype: 'determination',
                act: { kind: 'link', label: 'Review in the draft', record: { kind: 'obligation_source_proposal', id: p.decision_id }, href: `/programs/${encodeURIComponent(LEGAL_PROGRAM)}/scope` },
            }));
        }
    }
    // 9. V98-4-2: open determinations (law × system) → the legal reviewer; open fact questions → the asked owner.
    const grid = idx.frameworkProfile.has(LEGAL_PROGRAM) ? await buildGrid(db, workspaceId, now) : null;
    // V98-4 review T4-H1: the in-force gap items (keyed by the cell) vs other open remediation in the program —
    // counted BEFORE a waiting cell's Legal-held entries move to "waiting" below.
    let aiCounts = null;
    if (grid) {
        const gaps = entries.filter((e) => !!e.holder_user_id && isGap(e) && e.program_ids.includes(grid.program_id));
        const undecided = new Set(gaps.filter((e) => e.key.startsWith('determination:')).map((e) => e.key)).size;
        const other = new Set(gaps.filter((e) => !e.key.startsWith('determination:')).map((e) => e.key)).size;
        const open = grid.cells.filter((c) => c.open);
        const waiting = open.filter((c) => c.fact?.state === 'open').length;
        aiCounts = {
            program_id: grid.program_id, open: open.length, ready: open.length - waiting, waiting_on_owner: waiting,
            rule_changed: open.filter((c) => c.state === 'rule_changed').length, decided_in_draft: grid.decided_in_draft,
            undecided_in_force: undecided, non_compliant: other, proposals_open: proposals.results.length,
        };
    }
    if (grid && grid.cells.some((c) => c.open || c.fact?.state === 'open')) {
        const legal = await legalReviewers(db, workspaceId, now);
        const lawOf = new Map(grid.laws.map((l) => [l.law_key, l]));
        const sysOf = new Map(grid.systems.map((x) => [x.system_id, x]));
        for (const c of grid.cells) {
            const law = lawOf.get(c.law_key);
            const sys = sysOf.get(c.system_id);
            const key = `determination:${c.cell_id}`;
            const ref = { kind: 'determination', id: c.cell_id };
            const label = `${law.label} × ${sys.name}`;
            const href = `/programs/${encodeURIComponent(LEGAL_PROGRAM)}/scope?cell=${encodeURIComponent(c.cell_id)}`;
            if (c.fact?.state === 'open') {
                const askedTo = c.fact.asked_to_user_id;
                // V98-4 review: one shape for fact rows — Legal asks · "{the question, verbatim}" · {system}; action Answer.
                entries.push(base({
                    key, ref, title: `Legal asks · “${c.fact.question_text}” · ${sys.name}`.slice(0, 400), action: 'answer_fact', hat: 'system_owner',
                    holder_user_id: askedTo, waiting_user_ids: [c.fact.asked_by], program_ids: [grid.program_id], subtype: 'determination',
                    asked_at: c.fact.asked_at,
                    act: { kind: 'endpoint', label: 'Answer', record: { kind: 'fact_question', id: c.fact.question_id }, method: 'POST',
                        path: `/ai-program/fact-questions/${encodeURIComponent(c.fact.question_id)}/answer`, href },
                }));
                // T4-B1: while the question is out the cell waits on the owner — Legal's hold on it (the decision and the
                // in-force gap item) moves to "Waiting on others" instead of Owed. Counted once, held by the owner.
                for (let i = entries.length - 1; i >= 0; i--) {
                    const e = entries[i];
                    if (e.key === key && e.holder_user_id !== askedTo && (e.action === 'remediate' || e.action === 'decide')) {
                        entries.splice(i, 1);
                        const answer = entries.find((x) => x.key === key && x.action === 'answer_fact');
                        if (answer && e.holder_user_id && !answer.waiting_user_ids.includes(e.holder_user_id))
                            answer.waiting_user_ids.push(e.holder_user_id);
                    }
                }
                if (c.open)
                    continue;
            }
            if (!c.open)
                continue;
            entries.push(base({
                key, ref, title: `${c.state === 'rule_changed' ? 'Decide again (rule changed)' : 'Decide'} · ${label}`, action: 'decide', hat: 'legal_reviewer',
                holder_user_id: legal[0] ?? null, blocked: !legal[0], program_ids: [grid.program_id],
                waiting_user_ids: [], subtype: 'determination',
                act: { kind: 'link', label: 'Decide', record: ref, href },
            }));
        }
    }
    // 10. V103-3-5: GovRAMP ConMon / Snapshot cycles due within 30 days or late → the program lead
    // (else the first owner / admin). One entry per cycle; it closes when recorded as submitted.
    const cycles = await pCycles;
    const cycleHolder = new Map();
    for (const c of cycles) {
        if (!cycleHolder.has(c.profile_id))
            cycleHolder.set(c.profile_id, await conmonCycleHolder(db, workspaceId, c.profile_id, now));
        const holder = cycleHolder.get(c.profile_id) ?? null;
        const ref = { kind: 'conmon_cycle', id: c.cycle_id };
        entries.push(base({
            key: `conmon_cycle:${c.cycle_id}`, ref,
            title: `${c.effective_status === 'late' ? 'Late · ' : ''}${conmonCycleTitle(c, getProgramManifest(c.program)?.label ?? c.program)}`,
            action: 'complete_occurrence', hat: 'program_lead', holder_user_id: holder, blocked: !holder,
            due_at: conmonDueInstant(c.due_at), program_ids: [c.profile_id], period_ids: idx.periodProfile.has(c.period_id) ? [c.period_id] : [],
            subtype: 'task', act: { kind: 'link', label: 'Open the ConMon cycle', record: ref, href: conmonCycleHref(c.program, c.period_id) },
        }));
    }
    // 11. V105-6-7 H7: a target plan whose five parts are generated but whose current version is unsigned →
    // the first owner / admin (signing is owner / admin only). It closes once the version is signed.
    try {
        const plans = await pPlans;
        if (plans.length) {
            const signer = (await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND role IN ('owner', 'admin') AND removed_at IS NULL
          ORDER BY CASE role WHEN 'owner' THEN 0 ELSE 1 END, joined_at ASC, user_id ASC LIMIT 1`, [workspaceId])).results[0]?.user_id ?? null;
            for (const p of plans) {
                const ref = { kind: 'infra_plan', id: p.plan_id };
                entries.push(base({
                    key: `infra_plan_sign:${p.plan_id}:${p.plan_version_sha256}`, ref, title: `Sign the ${getProgramManifest(p.program)?.shortLabel ?? p.program} target plan`,
                    action: 'sign', hat: 'owner', holder_user_id: signer, blocked: !signer, program_ids: [p.profile_id],
                    subtype: 'task', act: { kind: 'link', label: 'Open the target plan', record: ref, href: `/programs/${encodeURIComponent(p.program)}/scope?view=plan` },
                }));
            }
        }
    }
    catch { /* plans not on this plane yet */ }
    // 12. D-106-18 / G2 (owner 2026-10-18): proposed results waiting on review in an open period of a
    // program the hourly evaluator runs for → ONE holder: the program's lead, else the workspace owner.
    // Others who may review still do so from the Prepare button; SoD stays the review service's (the
    // holder's own proposals are never counted for them).
    try {
        const pending = await pPending;
        const holders = new Map();
        let owner;
        const holderFor = async (profileId) => {
            if (holders.has(profileId))
                return holders.get(profileId);
            const lead = (await db.query(`SELECT user_id FROM assignments
          WHERE workspace_id = ? AND subject_kind = 'program' AND subject_id = ? AND responsibility = 'program_lead'
            AND effective_from <= ? AND (effective_to IS NULL OR effective_to > ?)
          ORDER BY effective_from ASC, user_id ASC LIMIT 1`, [workspaceId, profileId, iso, iso])).results[0]?.user_id ?? null;
            if (!lead && owner === undefined) {
                owner = (await db.query(`SELECT user_id FROM memberships WHERE workspace_id = ? AND role = 'owner' AND removed_at IS NULL ORDER BY joined_at ASC, user_id ASC LIMIT 1`, [workspaceId])).results[0]?.user_id ?? null;
            }
            const h = lead ?? owner ?? null;
            holders.set(profileId, h);
            return h;
        };
        const byPeriod = new Map();
        for (const r of pending.results) {
            if (!getProgramManifest(r.program)?.readiness.scheduledEvaluation)
                continue;
            const g = byPeriod.get(r.period_id) ?? { profile_id: r.profile_id, program: r.program, period_id: r.period_id, rows: [] };
            g.rows.push({ proposed_by: r.proposed_by, n: Number(r.n) });
            byPeriod.set(r.period_id, g);
        }
        for (const g of byPeriod.values()) {
            const holder = await holderFor(g.profile_id);
            const count = g.rows.filter((x) => x.proposed_by !== holder).reduce((a, x) => a + x.n, 0);
            if (!count)
                continue;
            const label = getProgramManifest(g.program)?.shortLabel ?? g.program;
            const ref = { kind: 'result_review', id: g.period_id };
            entries.push(base({
                key: `result_review:${g.period_id}`, ref, title: `Review ${count} proposed ${label} ${count === 1 ? 'result' : 'results'}`,
                action: 'review_rows', hat: 'program_lead', holder_user_id: holder, blocked: !holder, quantity: count,
                program_ids: [g.profile_id], period_ids: idx.periodProfile.has(g.period_id) ? [g.period_id] : [],
                waiting_user_ids: [...new Set(g.rows.map((x) => x.proposed_by).filter((u) => !u.startsWith('system:') && u !== holder))],
                subtype: 'task', act: { kind: 'link', label: 'Review the proposed results', record: ref, href: `/programs/${encodeURIComponent(g.program)}/prepare?panel=review` },
            }));
        }
    }
    catch { /* determinations not on this plane yet */ }
    // Merge exact duplicates (e.g. an ask seen both as a record and as its delivered work item).
    const merged = new Map();
    for (const e of entries) {
        const k = `${e.key}|${e.holder_user_id ?? ''}|${e.action}`;
        const cur = merged.get(k);
        if (!cur) {
            merged.set(k, { ...e, waiting_user_ids: [...new Set(e.waiting_user_ids)], program_ids: [...new Set(e.program_ids)].sort(), period_ids: [...new Set(e.period_ids)].sort() });
            continue;
        }
        cur.waiting_user_ids = [...new Set([...cur.waiting_user_ids, ...e.waiting_user_ids])];
        cur.program_ids = [...new Set([...cur.program_ids, ...e.program_ids])].sort();
        cur.period_ids = [...new Set([...cur.period_ids, ...e.period_ids])].sort();
        cur.due_at = minDate(cur.due_at, e.due_at);
        cur.hard_date = minDate(cur.hard_date, e.hard_date);
        cur.blocked = cur.blocked || e.blocked;
    }
    // An item's program set is the union over all its entries (so org / program counts agree).
    const progByKey = new Map();
    const periodByKey = new Map();
    for (const e of merged.values()) {
        for (const p of e.program_ids)
            (progByKey.get(e.key) ?? progByKey.set(e.key, new Set()).get(e.key)).add(p);
        for (const p of e.period_ids)
            (periodByKey.get(e.key) ?? periodByKey.set(e.key, new Set()).get(e.key)).add(p);
    }
    for (const e of merged.values()) {
        e.program_ids = [...(progByKey.get(e.key) ?? [])].sort();
        e.period_ids = [...(periodByKey.get(e.key) ?? [])].sort();
    }
    return { workspace_id: workspaceId, generated_at: iso, entries: [...merged.values()], program_keys: Object.fromEntries(idx.programKey), ai: aiCounts };
}
// ---- viewer projections ------------------------------------------------------------------
/** The viewer's owed list: each item once, under the next action they owe, urgency-sorted. */
export function owedBy(comp, userId, extraHats = new Map()) {
    const nowIso = comp.generated_at;
    const mine = new Map();
    for (const e of comp.entries)
        if (e.holder_user_id === userId)
            mine.set(e.key, [...(mine.get(e.key) ?? []), e]);
    const rows = [];
    for (const [key, list] of mine) {
        const sorted = [...list].sort((a, b) => ACTION_RANK[a.action] - ACTION_RANK[b.action] || urgency(a, b, nowIso));
        const top = sorted[0];
        const due = minDate(...list.map((e) => e.due_at));
        const hard = minDate(...list.map((e) => e.hard_date));
        const hats = [...new Set([top.hat, ...sorted.slice(1).map((e) => e.hat), ...(extraHats.get(key) ?? [])])];
        rows.push({
            key, ref: top.ref, title: top.title, action: top.action, hat: top.hat, hats,
            on_behalf_of_user_id: top.on_behalf_of_user_id, delegation_id: top.delegation_id,
            due_at: due, hard_date: hard, overdue: isOverdue({ due_at: due, hard_date: hard }, nowIso), blocks_filing: !!hard,
            blocked: top.blocked, quantity: top.quantity, program_ids: top.program_ids,
            then: [...new Set(sorted.slice(1).map((e) => e.action))].filter((a) => a !== top.action),
            ...(top.subtype ? { subtype: top.subtype, act: top.act } : {}),
            ...(top.about ? { about: top.about } : {}),
            ...(top.escalation ? { escalation: top.escalation } : {}),
            ...(top.asked_at ? { asked_at: top.asked_at } : {}),
        });
    }
    return rows.sort((a, b) => urgency(a, b, nowIso));
}
/** What the viewer is waiting on others for (not counted as owed). */
export function waitingFor(comp, userId) {
    const nowIso = comp.generated_at;
    const owedKeys = new Set(comp.entries.filter((e) => e.holder_user_id === userId).map((e) => e.key));
    const out = new Map();
    for (const e of comp.entries) {
        if (owedKeys.has(e.key) || !e.waiting_user_ids.includes(userId) || out.has(e.key))
            continue;
        const as = e.on_behalf_of_user_id === userId ? 'delegator'
            : e.ref.kind === 'coverage_ask' || e.action === 'answer_ask' ? 'asker'
                : e.action === 'answer_fact' ? 'asker'
                    : e.ref.kind === 'independent_check' ? 'self_approver'
                        : e.ref.kind === 'access_review' ? 'access_reviewer' : 'preparer';
        out.set(e.key, { key: e.key, ref: e.ref, title: e.title, as, waiting_on_user_id: e.holder_user_id,
            due_at: e.due_at, hard_date: e.hard_date, overdue: isOverdue(e, nowIso) });
    }
    return [...out.values()].sort((a, b) => urgency(a, b, nowIso));
}
/** The viewer's visibility over org / program totals. */
export async function countScopeFor(db, workspaceId, viewer, now = new Date()) {
    const iso = now.toISOString();
    const leads = await db.query(`SELECT DISTINCT subject_id FROM assignments WHERE workspace_id = ? AND user_id = ? AND subject_kind = 'program' AND responsibility = 'program_lead'
        AND effective_from <= ? AND (effective_to IS NULL OR effective_to > ?) ORDER BY subject_id ASC`, [workspaceId, viewer.user_id, iso, iso]);
    const lead_program_ids = leads.results.map((l) => l.subject_id);
    const scope = ['owner', 'admin'].includes(viewer.role) ? 'org' : lead_program_ids.length ? 'programs' : 'own';
    return { scope, lead_program_ids };
}
/**
 * THE shared counter (PRD §6: "Counts are equal across Today, the sidebar
 * and the program pages"). Each item counted once — by its key — wherever it
 * is counted.
 */
export function countWork(comp, viewerId, visibility) {
    const nowIso = comp.generated_at;
    const owed = owedBy(comp, viewerId);
    const waiting = waitingFor(comp, viewerId);
    // S8: one definition of "open" — an item with a holder-assigned next action, counted once.
    const items = new Map();
    for (const e of comp.entries) {
        if (!e.holder_user_id)
            continue;
        const cur = items.get(e.key);
        if (!cur) {
            items.set(e.key, { overdue: isOverdue(e, nowIso), programs: new Set(e.program_ids), primary: e });
            continue;
        }
        cur.overdue = cur.overdue || isOverdue(e, nowIso);
        for (const p of e.program_ids)
            cur.programs.add(p);
        const p = cur.primary;
        // R3-S1: an item the viewer holds is attributed to the viewer (their owner row = their "Yours");
        // otherwise the most urgent next action, then the holder id — each item still counted once.
        const mine = (x) => (x.holder_user_id === viewerId ? 0 : 1);
        const better = mine(e) - mine(p) || ACTION_RANK[e.action] - ACTION_RANK[p.action] || urgency(e, p, nowIso)
            || (e.holder_user_id < p.holder_user_id ? -1 : e.holder_user_id > p.holder_user_id ? 1 : 0);
        if (better < 0)
            cur.primary = e;
    }
    const all = [...items.values()];
    const primaryProgram = (i) => [...i.programs].sort()[0] ?? null;
    const programIds = new Set();
    for (const it of all)
        for (const p of it.programs)
            programIds.add(p);
    for (const r of owed)
        for (const p of r.program_ids)
            programIds.add(p);
    const visible = (p) => visibility.scope === 'org' || (visibility.scope === 'programs' && visibility.lead_program_ids.includes(p));
    for (const p of visibility.lead_program_ids)
        programIds.add(p);
    const per_program = [...programIds].sort().map((p) => {
        const inP = all.filter((i) => i.programs.has(p));
        const yours = owed.filter((r) => r.program_ids.includes(p)).length;
        if (!visible(p))
            return { program_id: p, yours, open: null, overdue: null, shared: null, shared_elsewhere: null };
        return {
            program_id: p, yours, open: inP.length, overdue: inP.filter((i) => i.overdue).length,
            shared: inP.filter((i) => i.programs.size > 1).length,
            shared_elsewhere: inP.filter((i) => primaryProgram(i) !== p).length,
        };
    }).filter((pc) => pc.yours > 0 || pc.open !== null);
    const ownerScope = visibility.scope === 'org' ? all
        : visibility.scope === 'programs'
            ? all.filter((i) => i.primary.holder_user_id === viewerId || [...i.programs].some((p) => visibility.lead_program_ids.includes(p)))
            : null;
    let by_owner = null;
    if (ownerScope) {
        const m = new Map();
        for (const i of ownerScope) {
            const u = i.primary.holder_user_id;
            const row = m.get(u) ?? { user_id: u, open: 0, overdue: 0 };
            row.open++;
            if (i.overdue)
                row.overdue++;
            m.set(u, row);
        }
        by_owner = [...m.values()].sort((a, b) => b.overdue - a.overdue || b.open - a.open || (a.user_id < b.user_id ? -1 : 1));
    }
    return {
        yours: { owed: owed.length, overdue: owed.filter((r) => r.overdue).length, waiting: waiting.length },
        org: visibility.scope === 'org'
            ? { open: all.length, overdue: all.filter((i) => i.overdue).length, shared: all.filter((i) => i.programs.size > 1).length,
                no_program: all.filter((i) => i.programs.size === 0).length }
            : null,
        by_owner,
        per_program,
        scope: visibility.scope,
    };
}
/** `/me/counts` — the same composer and counter as Today (+ the projected miss, S9). */
export async function getMyCounts(db, workspaceId, viewer, now = new Date()) {
    const [comp, vis, hats] = await Promise.all([
        collectWorkspaceWork(db, workspaceId, now), countScopeFor(db, workspaceId, viewer, now), listHats(db, workspaceId, viewer.user_id, now),
    ]);
    const counts = countWork(comp, viewer.user_id, vis);
    const next = await scopedNextHardDate(db, comp, viewer.user_id, vis, now);
    return { ...counts, will_miss: next.will_miss, verdict: viewerVerdict(comp, viewer.user_id, vis, next.will_miss, hats.responsibilities, now) };
}
/**
 * R3-S1: the viewer's ONE verdict (program-verdict-service) — Today, the
 * band, `/me/counts` and the Programs page all call this with the same
 * composition, so their status words agree. AI Regulatory legal gaps are a
 * count unless the viewer holds the legal reviewer hat.
 */
export function viewerVerdict(comp, viewerId, vis, willMiss, responsibilities, now) {
    const ai = Object.entries(comp.program_keys).filter(([, k]) => programHas(k, (m) => m.features.legalDeterminations)).map(([id]) => id);
    return computeScopeVerdict(comp, viewerId, vis, {
        now, will_miss: willMiss, ai_program_ids: ai, legal_titles_visible: responsibilities.includes('legal_reviewer'),
    });
}
/** The next hard date at the viewer's visibility (org; their programs + own; own) — Today, counts and blockers agree. */
export async function scopedNextHardDate(db, comp, viewerId, vis, now) {
    const owedKeys = new Set(owedBy(comp, viewerId).map((o) => o.key));
    const waitKeys = new Set(waitingFor(comp, viewerId).map((w) => w.key));
    const inScope = (e) => vis.scope === 'org' || owedKeys.has(e.key) || waitKeys.has(e.key)
        || (vis.scope === 'programs' && e.program_ids.some((p) => vis.lead_program_ids.includes(p)));
    const scopeEntries = comp.entries.filter(inScope);
    const scopeRequestIds = new Set(scopeEntries.filter((e) => e.ref.kind === 'signature_request').map((e) => e.ref.id));
    const allAtRisk = await signaturesAtRisk(db, comp.workspace_id, { now });
    const atRisk = vis.scope === 'org' ? allAtRisk : allAtRisk.filter((r) => scopeRequestIds.has(r.request_id));
    return { ...(await computeNextHardDate(db, comp, scopeEntries, atRisk, now)), at_risk: atRisk };
}
/**
 * Rule 23: the next hard date is the earliest hard date among items that
 * will actually miss — a non-delegable signature whose approver is back only
 * after it (coverage-service signaturesAtRisk), an item nobody is eligible to
 * act on, or an item whose holder is away until after it. Past hard dates are
 * already missed and are not "next".
 */
export async function computeNextHardDate(db, comp, scopeEntries, atRisk, now) {
    const today = utcDay(now);
    const misses = [];
    const seen = new Set();
    // The composer's item key for a signature request (artifact_kind:artifact_id), so a miss is one row everywhere.
    const sigKey = new Map(comp.entries.filter((e) => e.ref.kind === 'signature_request').map((e) => [e.ref.id, e.key]));
    for (const r of atRisk) {
        if (!r.fits && r.hard_date && r.hard_date >= today) {
            const key = sigKey.get(r.request_id) ?? `signature_request:${r.request_id}`;
            if (seen.has(key))
                continue;
            seen.add(key);
            misses.push({
                key, ref: { kind: 'signature_request', id: r.request_id }, title: r.title, hard_date: r.hard_date, reason: 'approver_away',
                user_id: r.approver_user_id, back_on: r.back_on, absence_state: r.absence_state,
                why: `can’t be delegated; the approver is away ${r.absent_from} – ${r.absent_to}${r.absence_state === 'proposed' ? ' (proposed)' : ''}, back ${r.back_on}`,
            });
        }
    }
    for (const e of scopeEntries) {
        const hard = e.hard_date?.slice(0, 10);
        if (!hard || hard < today || seen.has(e.key))
            continue;
        if (e.blocked) {
            misses.push({ key: e.key, ref: e.ref, title: e.title, hard_date: hard, reason: 'nobody_eligible', user_id: null, back_on: null, absence_state: null,
                why: 'nobody eligible to act' });
            seen.add(e.key);
            continue;
        }
        if (e.holder_user_id) {
            const away = await absenceOn(db, comp.workspace_id, e.holder_user_id, today);
            if (away && addDays(away.ends_on, 1) > hard) {
                misses.push({ key: e.key, ref: e.ref, title: e.title, hard_date: hard, reason: 'holder_away', user_id: e.holder_user_id,
                    back_on: addDays(away.ends_on, 1), absence_state: 'confirmed', why: `holder away until ${away.ends_on}` });
                seen.add(e.key);
            }
        }
    }
    misses.sort((a, b) => (a.hard_date < b.hard_date ? -1 : a.hard_date > b.hard_date ? 1 : a.key < b.key ? -1 : 1));
    const first = misses[0];
    const will_miss = first
        ? { key: first.key, ref: first.ref, title: first.title, date: first.hard_date, reason: first.reason, user_id: first.user_id,
            back_on: first.back_on, absence_state: first.absence_state, why: first.why }
        : null;
    return { next_hard_date: first?.hard_date ?? null, item: first ? { key: first.key, title: first.title, hard_date: first.hard_date, why: first.why } : null, misses, will_miss };
}
/** S9: non-delegable signatures the viewer owes whose hard date falls inside their own pending / confirmed absence. */
export async function signBeforeLeaving(db, workspaceId, owed, userId, now) {
    const sigs = owed.filter((o) => o.ref.kind === 'signature_request' && o.action === 'sign' && o.hard_date && isNonDelegable(o.key.split(':')[0]));
    if (sigs.length === 0)
        return [];
    const absences = await db.query(`SELECT * FROM absences WHERE workspace_id = ? AND user_id = ? AND state IN ('proposed', 'confirmed') AND ends_on >= ? ORDER BY starts_on ASC`, [workspaceId, userId, utcDay(now)]);
    const out = [];
    for (const o of sigs) {
        const hard = o.hard_date.slice(0, 10);
        const a = absences.results.find((x) => x.starts_on <= hard && x.ends_on >= hard);
        if (!a)
            continue;
        out.push({ key: o.key, ref: o.ref, title: o.title, artifact_kind: o.key.split(':')[0], hard_date: o.hard_date, due_at: o.due_at,
            absence_id: a.absence_id, absence_state: a.state, absent_from: a.starts_on, absent_to: a.ends_on });
    }
    return out.sort((a, b) => (a.hard_date < b.hard_date ? -1 : a.hard_date > b.hard_date ? 1 : a.key < b.key ? -1 : 1));
}
/** `/today` — the viewer's composed Today. */
export async function getToday(db, workspaceId, viewer, now = new Date()) {
    const [comp, vis, hats] = await Promise.all([
        collectWorkspaceWork(db, workspaceId, now),
        countScopeFor(db, workspaceId, viewer, now),
        listHats(db, workspaceId, viewer.user_id, now),
    ]);
    // Hats the viewer holds on the very subject an item acts on (e.g. owner of the policy they approve).
    const extra = new Map();
    for (const a of hats.assignments) {
        const k = `${a.subject_kind}:${a.subject_id}`;
        extra.set(k, [...new Set([...(extra.get(k) ?? []), a.responsibility])]);
    }
    const owed = owedBy(comp, viewer.user_id, extra);
    const waiting = waitingFor(comp, viewer.user_id);
    const isLead = vis.scope !== 'own';
    const { at_risk: atRisk, ...next } = await scopedNextHardDate(db, comp, viewer.user_id, vis, now);
    const verdict = viewerVerdict(comp, viewer.user_id, vis, next.will_miss, hats.responsibilities, now);
    const counts = { ...countWork(comp, viewer.user_id, vis), will_miss: next.will_miss, verdict };
    const signBefore = await signBeforeLeaving(db, workspaceId, owed, viewer.user_id, now);
    const upcoming = (await db.query(`SELECT * FROM absences WHERE workspace_id = ? AND user_id = ? AND state = 'confirmed' AND starts_on > ? ORDER BY starts_on ASC, absence_id ASC`, [workspaceId, viewer.user_id, utcDay(now)])).results.map((a) => ({
        absence_id: a.absence_id, starts_on: a.starts_on, ends_on: a.ends_on, state: 'confirmed',
        sign_before_leaving: signBefore.filter((s) => s.absence_id === a.absence_id),
        edit: { method: 'POST', path: `/coverage/absences/${encodeURIComponent(a.absence_id)}/edit` },
        cancel: { method: 'POST', path: `/coverage/absences/${encodeURIComponent(a.absence_id)}/end` },
    }));
    const heldHats = [...hats.responsibilities];
    const sectionHats = [...new Set([...heldHats, ...owed.map((o) => o.hat), ...waiting.map((w) => w.as)])];
    const sections = sectionHats.map((hat) => ({
        hat,
        owed: owed.filter((o) => o.hat === hat).length,
        waiting: waiting.filter((w) => w.as === hat).length,
        keys: owed.filter((o) => o.hat === hat).map((o) => o.key),
    }));
    return {
        generated_at: comp.generated_at,
        viewer: { user_id: viewer.user_id, hats: heldHats, lead_program_ids: vis.lead_program_ids, is_admin: vis.scope === 'org' },
        owed, waiting, sections, counts, next_hard_date: next,
        signatures_at_risk: isLead ? atRisk : null,
        sign_before_leaving: signBefore,
        verdict,
        upcoming_absences: upcoming,
        ai_legal: heldHats.includes('legal_reviewer') ? await legalTodaySummary(db, workspaceId, now, comp.ai ?? null) : null,
    };
}
//# sourceMappingURL=today-service.js.map